import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ViolationStateBlock from '../components/ViolationStateBlock';
import RuleLink from '../components/RuleLink';
import ZoneDeleteConfirmModal from './ZoneDeleteConfirmModal';
import ZonesOverview from './ZonesOverview';
import { buildZoneColorMap, zoneColorFrom } from '../../../utils/zoneColors';
import { useDrawingLogic } from '../../../hooks/useDrawingLogic';
import useCameraPreviewFrame from '../../../hooks/useCameraPreviewFrame';
import { createZone, deleteZone, listZones, updateZone } from '../../../services/zones';
import { describeHttpError } from '../../../utils/apiError';
import { describeConflict } from '../../../utils/conflictMessages';
import { MIN_POLYGON_POINTS, closePolygon, countUniquePoints } from '../../../utils/polygon';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';
import { countRu, POINT_FORMS } from '../../../utils/pluralRu';

/**
 * Зоны камеры со стабильным zone_id (Part 4, раздел 5 и 6.5–6.8).
 *
 * Идентичность зоны — zone_id из ответа бэкенда. Правка геометрии его не
 * меняет, индекс массива идентичностью не является и на фронте не
 * синтезируется. Рисование переиспользует существующий polygon-редактор
 * (useDrawingLogic): ТЗ требует сохранить его, а не писать новый.
 *
 * capability:
 *  - `direct`  — изменения уходят сразу в API зон;
 *  - `propose` — изменения уходят заявкой (onPropose), production не меняется;
 *  - `none`    — только просмотр.
 */

/**
 * FE-Z01: каноническое пространство — пиксели original-кадра; сам кадр
 * приходит из общего слоя useCameraPreviewFrame (B1). Helper оставлен здесь
 * как re-export для старых импортов.
 */
export { extractOriginalFrameUrl } from '../../../utils/cameraPreview';

/** FE-Z05: «Зона N», N — следующий номер в пределах камеры. Имя редактируемо. */
export const nextZoneName = (zones) => {
  const numbers = (Array.isArray(zones) ? zones : [])
    .map((zone) => /^Зона (\d+)$/.exec(zone?.display_name || ''))
    .filter(Boolean)
    .map((match) => Number(match[1]));
  const next = numbers.length ? Math.max(...numbers) + 1 : zones.length + 1;
  return `Зона ${next}`;
};

const ZoneEditor = ({ cameraId, capability = 'none', onPropose, onRulesChanged, onZonesChanged }) => {
  const drawing = useDrawingLogic();

  const [zones, setZones] = useState([]);
  const zoneColorMap = useMemo(() => buildZoneColorMap(zones), [zones]);
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');
  // B1: preview-кадр — общий слой с Change Request Detail, не локальный запрос.
  const preview = useCameraPreviewFrame(cameraId);

  const [editing, setEditing] = useState(null); // { zoneId, name, version }
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  // FE-FIX08: notice здесь всегда успех («Зона создана», «Зона удалена»),
  // ошибки живут в отдельном состоянии и сами не гаснут.
  useAutoDismiss(notice, setNotice);

  const [deleteTarget, setDeleteTarget] = useState(null); // { zone, dependentRules: [] | null }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const readOnly = capability === 'none';

  const loadZones = useCallback(() => {
    if (!cameraId) return Promise.resolve();
    setPhase('loading');

    return listZones(cameraId)
      .then((data) => {
        setZones(Array.isArray(data?.items) ? data.items : []);
        setPhase('ready');
      })
      .catch((error) => {
        setZones([]);
        setErrorMessage(describeHttpError(error, 'Не удалось загрузить зоны камеры'));
        setPhase('error');
      });
  }, [cameraId]);

  useEffect(() => { loadZones(); }, [loadZones]);

  // Смена камеры сбрасывает редактирование: зона другой камеры здесь не живёт.
  useEffect(() => {
    setEditing(null);
    setActionError('');
    setNotice('');
    drawing.resetDrawingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId]);

  const startCreate = () => {
    setActionError('');
    setNotice('');
    setEditing({ zoneId: null, name: nextZoneName(zones), version: null });
    drawing.loadZones([[]]);
    drawing.toggleDrawing();
  };

  const startEdit = (zone) => {
    setActionError('');
    setNotice('');
    setEditing({ zoneId: zone.zone_id, name: zone.display_name || '', version: zone.version });
    drawing.loadZones([zone.polygon || []]);
  };

  const cancelEditing = () => {
    setEditing(null);
    drawing.resetDrawingState();
  };

  const currentPolygon = drawing.zones[0] || [];

  const handleSave = () => {
    if (!editing) return;

    if (!editing.name.trim()) {
      setActionError('Укажите название зоны');
      return;
    }

    const unique = countUniquePoints(currentPolygon);
    if (unique < MIN_POLYGON_POINTS) {
      setActionError(`Контур должен содержать не меньше ${MIN_POLYGON_POINTS} разных точек`);
      return;
    }

    // FE-Z02: незамкнутый контур в backend не уходит — замыкание идемпотентно
    // и молчаливо, отдельного уведомления после сохранения не показываем.
    const polygon = closePolygon(currentPolygon);

    setSaving(true);
    setActionError('');

    const isNew = !editing.zoneId;

    // Заявка вместо прямого действия: production-состояние до одобрения
    // администратора не меняется (ТЗ 7.1).
    if (capability === 'propose') {
      const changeType = isNew ? 'zone_create' : 'zone_update';
      const payload = isNew
        ? { display_name: editing.name.trim(), polygon }
        : {
          zone_id: editing.zoneId,
          expected_version: editing.version,
          display_name: editing.name.trim(),
          polygon,
        };

      Promise.resolve(onPropose ? onPropose(changeType, payload) : undefined)
        .then(() => {
          setNotice('Изменение отправлено на одобрение администратора');
          cancelEditing();
        })
        .catch((error) => setActionError(describeHttpError(error, 'Не удалось отправить заявку')))
        .finally(() => setSaving(false));
      return;
    }

    const request = isNew
      ? createZone(cameraId, { display_name: editing.name.trim(), polygon })
      : updateZone(cameraId, editing.zoneId, {
        expected_version: editing.version,
        display_name: editing.name.trim(),
        polygon,
      });

    request
      .then(() => {
        cancelEditing();
        setNotice(isNew ? 'Зона создана' : 'Зона обновлена');
        if (onZonesChanged) onZonesChanged();
        return loadZones();
      })
      .catch((error) => {
        const conflict = describeConflict(error, 'zone');
        if (conflict.kind === 'not_conflict') {
          setActionError(describeHttpError(error, 'Не удалось сохранить зону'));
          return undefined;
        }

        setActionError(conflict.message);
        if (conflict.kind === 'version') {
          // Чужая правка уже применена backend'ом — перечитываем, а не
          // перезаписываем её текущим (устаревшим) вводом пользователя.
          cancelEditing();
          return loadZones();
        }
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  /**
   * FE-Z06: первый DELETE — без force. 409 zone_used_by_active_rules приносит
   * от backend точный список зависимых правил; повторный DELETE с force=true
   * уходит только после явного подтверждения во втором модальном окне.
   */
  const runDelete = (zone, { force = false } = {}) => {
    setDeleting(true);
    setDeleteError('');

    return deleteZone(cameraId, zone.zone_id, { expectedVersion: zone.version, force })
      .then(() => {
        setDeleteTarget(null);
        setNotice(force ? 'Зона удалена, зависимые правила деактивированы.' : 'Зона удалена');
        if (onRulesChanged) onRulesChanged();
        if (onZonesChanged) onZonesChanged();
        return loadZones();
      })
      .catch((error) => {
        const conflict = describeConflict(error, 'zone');
        if (conflict.kind === 'dependency' && !force) {
          setDeleteTarget({ zone, dependentRules: conflict.detail.dependent_rules });
          return undefined;
        }

        setDeleteTarget(null);
        if (conflict.kind === 'not_conflict') {
          setActionError(describeHttpError(error, 'Не удалось удалить зону'));
          return undefined;
        }

        // version / stale / legacy: перечитать authoritative-состояние, ничего не скрывать.
        setActionError(conflict.message);
        if (conflict.kind === 'stale' && onRulesChanged) onRulesChanged();
        return loadZones();
      })
      .finally(() => setDeleting(false));
  };

  /** Состояния preview-кадра, общие для обзора зон и редактора; null — кадр готов. */
  const renderPreviewState = () => {
    if (preview.phase === 'loading') return <p className="vio-field-hint">Загружаем кадр камеры…</p>;

    // B1: ошибка запроса — не «кадров нет». Backend может иметь кадры, и
    // утверждать обратное нельзя; даём повторить запрос.
    if (preview.phase === 'error') {
      return (
        <p className="vio-field-hint vio-field-hint--warning">
          Не удалось загрузить кадр камеры.{' '}
          <button type="button" className="vio-btn vio-btn--link" onClick={preview.reload}>
            Повторить
          </button>
        </p>
      );
    }

    if (preview.phase !== 'ready') {
      return (
        <p className="vio-field-hint vio-field-hint--warning">
          Кадров пока нет — зону не на чем рисовать. Кадры появятся после запуска камеры.
        </p>
      );
    }

    return null;
  };

  const renderCanvas = () => {
    const state = renderPreviewState();
    if (state) return state;

    return (
      <div className="vio-zone-canvas">
        <img
          ref={drawing.imageRef}
          src={preview.url}
          alt="Последний кадр"
          onLoad={drawing.initializeDrawing}
          style={{ display: 'none' }}
        />
        <canvas
          ref={drawing.canvasRef}
          onClick={drawing.handleCanvasClick}
          style={{ cursor: drawing.isDrawing ? 'crosshair' : 'default' }}
        />
        <div className="vio-zone-canvas-actions">
          <button type="button" className="vio-btn" onClick={drawing.toggleDrawing} disabled={saving}>
            {drawing.isDrawing ? 'Пауза' : 'Ставить точки'}
          </button>
          <button
            type="button"
            className="vio-btn"
            disabled={saving}
            onClick={() => {
              const result = drawing.finishDrawing();
              if (!result.ok) setActionError('Для замыкания нужно не меньше 3 точек');
            }}
          >
            Замкнуть контур
          </button>
          <button type="button" className="vio-btn" onClick={drawing.resetActiveZone} disabled={saving}>
            Очистить
          </button>
          <span className="vio-field-hint">{countRu(currentPolygon.length, POINT_FORMS)}</span>
        </div>
      </div>
    );
  };

  if (phase === 'loading') return <div className="vio-card"><ViolationStateBlock state="loading" /></div>;

  if (phase === 'error') {
    return (
      <div className="vio-card">
        <ViolationStateBlock state="error" message={errorMessage} onRetry={loadZones} />
      </div>
    );
  }

  return (
    <div className="vio-card">
      <p className="vio-drawer-section-title">Зоны контроля камеры</p>

      {actionError && <p className="vio-field-error">{actionError}</p>}
      {notice && <p className="vio-notice">{notice}</p>}

      {zones.length === 0 ? (
        <p className="vio-field-hint">Зоны не заданы</p>
      ) : (
        <>
          {/* Обзор всех зон на одном кадре; во время редактирования его
              место занимает канвас редактора, чтобы не путать два холста. */}
          {!editing && (renderPreviewState() || <ZonesOverview url={preview.url} zones={zones} />)}
            <ul className="vio-zone-list">
            {zones.map((zone, index) => (
              <li key={zone.zone_id} className="vio-zone">
                <div className="vio-zone-head">
                  <span className="vio-feature-name">
                    <span
                      className="vio-zone-swatch"
                      style={{ background: zoneColorFrom(zoneColorMap, zone, index).point }}
                      aria-hidden="true"
                    />
                    {zone.display_name || zone.zone_id}
                  </span>
                  <span className="vio-field-hint">
                    {countRu(Array.isArray(zone.polygon) ? zone.polygon.length : 0, POINT_FORMS)} · версия {zone.version}
                  </span>
                </div>

                {Array.isArray(zone.used_by_rules) && zone.used_by_rules.length > 0 && (
                  <div className="vio-feature-rules">
                    <span className="vio-feature-rules-title">Используется правилами:</span>
                    <ul className="vio-feature-rules-list">
                      {zone.used_by_rules.map((rule) => (
                        <li key={rule.rule_id}><RuleLink rule={rule} /></li>
                      ))}
                    </ul>
                  </div>
                )}

                {!readOnly && (
                  <div className="vio-cell-actions">
                    <button
                      type="button"
                      className="vio-btn vio-btn--link"
                      disabled={saving || deleting}
                      onClick={() => startEdit(zone)}
                    >
                      Изменить
                    </button>
                    {capability === 'direct' && (
                      <button
                        type="button"
                        className="vio-btn vio-btn--danger-link"
                        disabled={saving || deleting}
                        onClick={() => {
                          setDeleteError('');
                          setActionError('');
                          setNotice('');
                          setDeleteTarget({ zone, dependentRules: null });
                        }}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
            </ul>
        </>
      )}

      {!readOnly && !editing && (
        <div className="vio-filters-actions">
          <button type="button" className="vio-btn vio-btn--primary" onClick={startCreate}>
            Добавить зону
          </button>
        </div>
      )}

      {editing && (
        <div className="vio-zone-editor">
          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-zone-name">Название зоны</label>
            <input
              id="vio-zone-name"
              type="text"
              maxLength={255}
              value={editing.name}
              disabled={saving}
              onChange={(event) => setEditing((prev) => ({ ...prev, name: event.target.value }))}
              onKeyDown={(event) => {
                // ADMIN-ENTER01: в AdminModalForm ZoneEditor смонтирован внутри
                // <form onSubmit> формы камеры (finding 2). Без preventDefault
                // Enter в этом текстовом input сабмитил бы её и закрывал модалку,
                // молча теряя несохранённую геометрию зоны — вместо этого Enter
                // сохраняет саму зону.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
          </div>

          {renderCanvas()}

          <div className="vio-filters-actions">
            <button type="button" className="vio-btn" disabled={saving} onClick={cancelEditing}>
              Отмена
            </button>
            <button type="button" className="vio-btn vio-btn--primary" disabled={saving} onClick={handleSave}>
              {saving ? 'Сохраняем…' : capability === 'propose' ? 'Предложить изменение' : 'Сохранить зону'}
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ZoneDeleteConfirmModal
          zoneName={deleteTarget.zone.display_name || deleteTarget.zone.zone_id}
          dependentRules={deleteTarget.dependentRules || deleteTarget.zone.used_by_rules || []}
          mode={deleteTarget.dependentRules ? 'dependency' : 'warning'}
          busy={deleting}
          error={deleteError}
          onConfirm={() => (deleteTarget.dependentRules
            ? runDelete(deleteTarget.zone, { force: true })
            : runDelete(deleteTarget.zone))}
          onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
        />
      )}
    </div>
  );
};

export default ZoneEditor;
